/**
 * Date utilities for consistent timezone handling across the application.
 * All dates are stored in UTC and displayed in IST (Asia/Kolkata).
 */

// IST timezone identifier
const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Format a date string or Date object for display in IST timezone
 * @param {string|Date} dateStr - Date string (ISO format) or Date object
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Include time in output (default: false)
 * @param {string} options.format - Format style: 'short' (dd-Mon-yyyy), 'long' (dd Month yyyy), 'iso' (yyyy-mm-dd)
 * @returns {string} Formatted date string in IST
 */
export const formatDateIST = (dateStr, options = {}) => {
  const { includeTime = false, format = 'short' } = options;
  
  if (!dateStr) return "-";
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "-";
    
    // Format options for IST
    const dateOptions = {
      timeZone: IST_TIMEZONE,
      day: '2-digit',
      month: format === 'long' ? 'long' : 'short',
      year: 'numeric',
    };
    
    if (includeTime) {
      dateOptions.hour = '2-digit';
      dateOptions.minute = '2-digit';
      dateOptions.hour12 = true;
    }
    
    if (format === 'iso') {
      // Return yyyy-mm-dd format
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: IST_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      return formatter.format(date);
    }
    
    // Use Intl.DateTimeFormat for proper IST conversion
    const formatter = new Intl.DateTimeFormat('en-IN', dateOptions);
    const parts = formatter.formatToParts(date);
    
    // Build the output string based on format
    const day = parts.find(p => p.type === 'day')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';
    
    let result = `${day}-${month}-${year}`;
    
    if (includeTime) {
      const hour = parts.find(p => p.type === 'hour')?.value || '';
      const minute = parts.find(p => p.type === 'minute')?.value || '';
      const dayPeriod = parts.find(p => p.type === 'dayPeriod')?.value || '';
      result += ` ${hour}:${minute} ${dayPeriod}`;
    }
    
    return result;
  } catch (e) {
    console.error('Date formatting error:', e);
    return "-";
  }
};

/**
 * Format a date for HTML date input (YYYY-MM-DD) in IST timezone
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Date in YYYY-MM-DD format (IST)
 */
export const formatDateForInput = (dateStr) => {
  if (!dateStr) return "";
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    
    // Use Intl.DateTimeFormat with IST timezone to get correct date
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: IST_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    
    return formatter.format(date);
  } catch (e) {
    console.error('Date input formatting error:', e);
    return "";
  }
};

/**
 * Format a date for HTML datetime-local input in IST timezone
 * @param {string|Date} dateStr - Date string or Date object (default: current time)
 * @returns {string} Datetime in YYYY-MM-DDTHH:mm format (IST)
 */
export const formatDateTimeForInput = (dateStr = new Date()) => {
  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    if (isNaN(date.getTime())) return "";
    
    // Get IST components
    const options = {
      timeZone: IST_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    
    const formatter = new Intl.DateTimeFormat('en-CA', options);
    const parts = formatter.formatToParts(date);
    
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    const hour = parts.find(p => p.type === 'hour')?.value;
    const minute = parts.find(p => p.type === 'minute')?.value;
    
    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch (e) {
    console.error('DateTime input formatting error:', e);
    return "";
  }
};

/**
 * Get the current date in IST as YYYY-MM-DD
 * @returns {string} Current date in YYYY-MM-DD format (IST)
 */
export const getCurrentDateIST = () => {
  return formatDateForInput(new Date());
};

/**
 * Get the current datetime in IST as YYYY-MM-DDTHH:mm
 * @returns {string} Current datetime in YYYY-MM-DDTHH:mm format (IST)
 */
export const getCurrentDateTimeIST = () => {
  return formatDateTimeForInput(new Date());
};

/**
 * Parse a date string from an input and return an ISO string for storage
 * The input is assumed to be in IST timezone
 * @param {string} dateStr - Date in YYYY-MM-DD format (IST)
 * @param {string} timeStr - Optional time in HH:mm format (IST)
 * @returns {string} ISO date string (UTC)
 */
export const parseISTDateToUTC = (dateStr, timeStr = '00:00') => {
  if (!dateStr) return null;
  
  try {
    // Create a date string that JavaScript will parse as IST
    // By appending the IST offset (+05:30)
    const isoStr = `${dateStr}T${timeStr}:00+05:30`;
    const date = new Date(isoStr);
    
    if (isNaN(date.getTime())) return null;
    
    return date.toISOString();
  } catch (e) {
    console.error('Date parsing error:', e);
    return null;
  }
};

/**
 * Format time only from a date string in IST
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {string} Time in HH:mm AM/PM format (IST)
 */
export const formatTimeIST = (dateStr) => {
  if (!dateStr) return "-";
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "-";
    
    const formatter = new Intl.DateTimeFormat('en-IN', {
      timeZone: IST_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    
    return formatter.format(date);
  } catch (e) {
    console.error('Time formatting error:', e);
    return "-";
  }
};
